import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { serviceAreas } from '../../../mocks/home';

const ServiceAreas = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 bg-white" id="service-areas">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="h-0.5 w-8 bg-teal"></div>
              <span className="text-teal font-body text-xs tracking-widest uppercase font-medium">Coverage Area</span>
            </div>
            <h2 className="font-heading font-800 text-navy text-4xl lg:text-5xl mb-6 leading-tight">
              Serving Minneapolis &amp; the Twin Cities
            </h2>
            <p className="text-charcoal/60 font-body text-lg leading-relaxed mb-8">
              TrueCare Moving Services operates throughout the greater Minneapolis-Saint Paul metropolitan area. Whether you&apos;re moving within Minneapolis or to a surrounding city, our team is ready to help.
            </p>

            {/* City Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-8">
              {serviceAreas.map((city, i) => (
                <Link
                  key={city}
                  to={`/locations/${city.toLowerCase().replace(/[\s.]/g, '-')}`}
                  className={`flex items-center gap-2 py-2.5 px-3 rounded-lg bg-cream hover:bg-teal/10 border border-transparent hover:border-teal/25 text-charcoal hover:text-teal transition-all duration-200 group cursor-pointer ${
                    visible ? 'opacity-100' : 'opacity-0'
                  }`}
                  style={{ transitionDelay: visible ? `${i * 30}ms` : '0ms' }}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-map-pin-2-fill text-teal text-xs"></i>
                  </div>
                  <span className="font-body text-sm font-medium whitespace-nowrap">{city}</span>
                </Link>
              ))}
            </div>

            <p className="text-charcoal/50 font-body text-sm">
              Not seeing your city?{' '}
              <a href="tel:+16123674481" className="text-teal hover:underline cursor-pointer font-medium">
                Call us at (612) 367-4481
              </a>
              {' '}— we cover many additional areas.
            </p>
          </div>

          {/* Right - Map */}
          <div className={`transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="rounded-2xl overflow-hidden border border-navy/10 h-[500px] relative">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d91234.25892034168!2d-93.3289717!3d44.9774859!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x52b333909377bbff%3A0x1b2bda9fe47ac434!2sMinneapolis%2C%20MN!5e0!3m2!1sen!2sus!4v1710000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="TrueCare Moving Services Coverage Area - Minneapolis, MN"
                className="w-full h-full"
              ></iframe>
            </div>
            {/* Address Card */}
            <div className="mt-4 bg-navy rounded-xl px-5 py-4 flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-teal/20 rounded-lg">
                <i className="ri-map-pin-line text-teal text-lg"></i>
              </div>
              <div>
                <div className="font-heading font-700 text-white text-sm">TrueCare Moving Services LLC</div>
                <div className="text-text-muted font-body text-xs mt-0.5">121 Washington Ave S #332, Minneapolis, MN 55401</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreas;
